import { Banner } from "./banner";
import {
	activateBlocker,
	getBlockedScripts,
	revokeCategory,
	updateBlockerConsent,
} from "./blocker";
import { loadConfig } from "./config-loader";
import {
	acceptAllChoices,
	buildChoicesFromState,
	deleteConsentCookie,
	needsReconsent,
	readConsent,
	rejectAllChoices,
	writeConsent,
} from "./consent-state";
import { mergeDomainMappings } from "./domain-map";
import { emitConsent, emitReady } from "./events";
import { mountFooter } from "./footer";
import { pushConsentDefault, pushConsentUpdate } from "./gcm";
import { ReopenButton } from "./reopen-button";
import { flushReportQueue, reportConsent } from "./reporter";
import type { ConsentState, CookieCategory, CookieGuardGlobal, WidgetConfig } from "./types";

let currentConsent: ConsentState | null = null;
let banner: Banner | null = null;
let reopenButton: ReopenButton | null = null;
let widgetConfig: WidgetConfig | null = null;
let apiKey = "";
let apiBase = "";

// Capture during synchronous execution — null inside async callbacks
const selfScript = document.currentScript as HTMLScriptElement | null;

// ── Synchronous blocking init ──────────────────────────────────────────────────
// These MUST run before any other script on the page.

// Blocking (and the GCM defaults it feeds) is DEFAULT-ON: a stripped or
// mistyped data-blocking-mode attribute must degrade to the compliant
// behavior, not to a cosmetic consent recorder. Only an explicit
// data-blocking-mode="off" disables enforcement.
const blockingMode = selfScript?.getAttribute("data-blocking-mode") ?? "auto";
const isAutoBlocking = blockingMode !== "off";

if (isAutoBlocking) {
	// Read existing consent synchronously from cookie
	const earlyConsent = readConsent();
	const earlyChoices = earlyConsent ? buildChoicesFromState(earlyConsent) : null;

	// Push GCM default BEFORE gtag.js loads
	pushConsentDefault(earlyChoices);

	// Activate blocker synchronously — intercepts all subsequent scripts
	activateBlocker(earlyChoices);
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function resolveScriptParams(): { apiKey: string; apiBase: string } {
	const script = selfScript;
	const key = script?.getAttribute("data-api-key") ?? "";
	const base = script?.getAttribute("data-api-base") ?? detectApiBase(script);
	return { apiKey: key, apiBase: base };
}

function detectApiBase(script: HTMLScriptElement | null): string {
	if (script?.src) {
		try {
			const url = new URL(script.src);
			return `${url.protocol}//${url.host}/api/v1`;
		} catch {
			// invalid URL
		}
	}
	return "/api/v1";
}

function handleConsent(
	choices: Record<CookieCategory, boolean>,
	action: "accept_all" | "reject_all" | "custom",
): void {
	const configVersion = String(widgetConfig?.version);
	currentConsent = writeConsent(apiKey, choices, action, configVersion);
	reportConsent(apiBase, apiKey, choices, action, widgetConfig?.version ?? 0);

	// Update blocker — release or maintain blocks
	if (isAutoBlocking) {
		updateBlockerConsent(choices);
		pushConsentUpdate(choices);
	}

	emitConsent(currentConsent);
}

/**
 * Floating reopen button: shown only when a consent decision exists, the
 * banner is hidden, and branding.reopen is enabled.
 */
function showReopenIfEligible(): void {
	if (!widgetConfig?.config.branding.reopen) return;
	if (!currentConsent) return;

	if (!reopenButton) {
		const cfg = widgetConfig.config;
		reopenButton = new ReopenButton({
			colors: {
				primary: cfg.colors.primary,
				background: cfg.colors.background,
				border: cfg.colors.border,
			},
			position: cfg.branding.reopenPosition,
			language: cfg.language,
				logo: cfg.branding.logo,
				logoAlt: cfg.branding.logoAlt,
			onClick() {
				if (!banner) return;
				if (currentConsent) {
					banner.setToggles(buildChoicesFromState(currentConsent));
				}
				reopenButton?.hide();
				banner.show();
			},
		});
		reopenButton.mount();
	}

	reopenButton.show();
}

// ── Async init ─────────────────────────────────────────────────────────────────

async function init(): Promise<void> {
	const params = resolveScriptParams();
	apiKey = params.apiKey;
	apiBase = params.apiBase;

	if (!apiKey) {
		console.warn("[CookieGuard] Missing data-api-key attribute on script tag.");
		return;
	}

	// Flush any queued offline reports
	void flushReportQueue();

	// Load config
	widgetConfig = await loadConfig(apiKey, apiBase);

	// Merge scan-discovered domain mappings into the blocker
	if (widgetConfig.cookies.length > 0) {
		const mappings = widgetConfig.cookies
			.filter((c) => c.domain)
			.map((c) => ({ domain: c.domain as string, category: c.category }));
		mergeDomainMappings(mappings);
	}

	// Read existing consent
	currentConsent = readConsent();

	// Check if re-consent is needed (config version changed)
	const configVersion = String(widgetConfig.version);
	const shouldShow = needsReconsent(currentConsent, configVersion);

	// Create banner
	banner = new Banner(
		widgetConfig.config,
		{
			onAcceptAll() {
				handleConsent(acceptAllChoices(), "accept_all");
			},
			onRejectAll() {
				handleConsent(rejectAllChoices(), "reject_all");
			},
			onCustom(choices: Record<CookieCategory, boolean>) {
				handleConsent(choices, "custom");
			},
			onHide() {
				// After any consent action (or programmatic hide), surface the
				// floating reopen button.
				showReopenIfEligible();
			},
		},
		widgetConfig.detectedCookies ?? [],
	);

	// If existing consent, pre-fill toggles
	if (currentConsent && !shouldShow) {
		banner.setToggles(buildChoicesFromState(currentConsent));
	}

	// Render the footer trust kit (ANPC SOL/SAL + payment marks). Independent of
	// consent — it's always-on site furniture, not gated on a banner choice.
	mountFooter(widgetConfig.config.footer, widgetConfig.config.language);

	// Emit ready
	emitReady(currentConsent);

	// Show banner if needed; otherwise consent already exists → offer reopen
	if (shouldShow) {
		banner.show();
	} else {
		showReopenIfEligible();
	}
}

// ── Public API ──────────────────────────────────────────────────────────────

const CookieGuard: CookieGuardGlobal = {
	_initialized: false,

	get consent(): ConsentState | null {
		return currentConsent;
	},

	show(): void {
		if (banner) {
			if (currentConsent) {
				banner.setToggles(buildChoicesFromState(currentConsent));
			}
			reopenButton?.hide();
			banner.show();
		}
	},

	revokeAll(): void {
		deleteConsentCookie();

		// Audit the withdrawal server-side (GDPR Art. 7(3)): log an all-rejected
		// record so the proof-of-consent export shows consent was revoked at T.
		if (apiKey && apiBase) {
			reportConsent(apiBase, apiKey, rejectAllChoices(), "reject_all", widgetConfig?.version ?? 0);
		}

		// Revoke each non-necessary category
		if (isAutoBlocking && widgetConfig) {
			const categories: CookieCategory[] = ["functional", "analytics", "marketing"];
			for (const cat of categories) {
				revokeCategory(cat, widgetConfig.cookies);
			}

			// Reset blocker to deny-all
			updateBlockerConsent({
				necessary: true,
				functional: false,
				analytics: false,
				marketing: false,
			});

			pushConsentUpdate({
				necessary: true,
				functional: false,
				analytics: false,
				marketing: false,
			});
		}

		currentConsent = null;

		if (banner) {
			banner.setToggles({
				necessary: true,
				functional: false,
				analytics: false,
				marketing: false,
			});
			reopenButton?.hide();
			banner.show();
		}
	},

	get blockedScripts() {
		return getBlockedScripts();
	},
};

// Expose on window
(window as unknown as Record<string, unknown>).CookieGuard = CookieGuard;

// Auto-init when DOM is ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => {
		void init();
	});
} else {
	void init();
}
